import React from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaWhatsapp, FaArrowRight } from 'react-icons/fa';
import { HiLightningBolt, HiUserGroup, HiTrendingUp, HiShieldCheck } from 'react-icons/hi';
import { Link } from 'react-router-dom';

const About = () => {
  // WhatsApp configuration
  const whatsappNumber = '918700186979';
  const whatsappMessage = 'Hello! I would like to learn more about SkyMuse digital marketing services.';
  const openWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  const features = [
    { icon: HiLightningBolt, text: 'Fast & Efficient Solutions' },
    { icon: HiUserGroup, text: 'Dedicated Expert Team' },
    { icon: HiTrendingUp, text: 'Proven Growth Strategies' },
    { icon: HiShieldCheck, text: 'Transparent Reporting' },
  ];

  const benefits = [
    'Data-driven marketing strategies',
    'Customized solutions for your business',
    '24/7 dedicated support team',
    'Measurable results & ROI tracking',
    'Industry-leading expertise',
    'Continuous optimization & improvement',
  ];

  return (
    <section id="about" className="py-24 bg-gradient-to-br from-white via-purple-50/20 to-blue-50/30 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-gradient-to-l from-purple-200 to-transparent rounded-full blur-[150px] opacity-60 -translate-y-1/2" />
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-gradient-to-br from-blue-200 to-cyan-200 rounded-full blur-[120px] opacity-50" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Visual Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Main Card */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-pink-500 rounded-3xl transform rotate-3 scale-105" />

              <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 overflow-hidden">
                <div className="text-center py-12">
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-4"
                  >
                    5+
                  </motion.div>
                  <div className="text-2xl font-semibold text-white mb-2">Years of Excellence</div>
                  <div className="text-gray-400">Digital Marketing Expertise</div>
                </div>
              </div>
            </div>

            {/* Floating Cards */}
            <motion.div
              animate={{
                y: [-10, 10, -10],
                rotate: [2, -2, 2]
              }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -top-12 -right-12 bg-white/95 backdrop-blur-sm rounded-2xl p-5 shadow-2xl border border-white/50 z-10 hidden md:block"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <HiTrendingUp className="text-green-600 text-2xl" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">500+</div>
                  <div className="text-gray-500 text-sm font-medium">Brands Served</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              animate={{
                y: [10, -10, 10],
                rotate: [-2, 2, -2]
              }}
              transition={{ duration: 5, repeat: Infinity }}
              className="absolute -bottom-12 -left-12 bg-white/95 backdrop-blur-sm rounded-2xl p-5 shadow-2xl border border-white/50 z-10 hidden md:block"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <HiUserGroup className="text-blue-600 text-2xl" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">98%</div>
                  <div className="text-gray-500 text-sm font-medium">Client Satisfaction</div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-2 bg-purple-100 text-purple-600 rounded-full text-sm font-semibold mb-6"
            >
              About SkyMuse
            </motion.span>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              We Help Businesses
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500"> Grow & Succeed</span>
            </h2>

            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              With over 5 years of experience in digital marketing, we've helped hundreds of
              businesses transform their online presence and achieve remarkable growth. Our
              team of experts combines creativity with data-driven strategies to deliver
              exceptional results.
            </p>

            {/* Feature Grid */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-purple-50 transition-colors group"
                >
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                    <feature.icon className="text-purple-600 text-xl" />
                  </div>
                  <span className="text-gray-700 font-medium text-sm">{feature.text}</span>
                </motion.div>
              ))}
            </div>

            {/* Benefits List */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <FaCheckCircle className="text-green-500 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">{benefit}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <motion.button
                onClick={openWhatsApp}
                whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(34, 197, 94, 0.3)' }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-full shadow-lg flex items-center gap-2 cursor-pointer"
              >
                <FaWhatsapp className="text-xl" />
                Chat With Us
              </motion.button>
              <Link to="/about">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-full hover:border-purple-500 hover:text-purple-600 transition-colors flex items-center gap-2"
                >
                  Learn More
                  <FaArrowRight />
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
